import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/pro-light-svg-icons/faBan';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { AddBlockInput, BlockSuggestion, useBlocks } from '../hooks/useBlocks';
import { QuestionPanel } from './QuestionPanel';
import { NeverContactChips } from './NeverContactChips';
import { whoMessages as messages } from './whoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Who.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ALWAYS_SKIPPED = [messages.skipMoney, messages.skipBought, messages.skipStop, messages.skipEditorial];

/** The reason of a refused add (`cause.reason` of the tRPC error), else null. */
function reasonOf(error: unknown): string | null {
  const data = (error as { data?: { reason?: unknown } } | null)?.data;
  if (typeof data?.reason === 'string') {
    return data.reason;
  }
  const cause = (error as { cause?: { reason?: unknown } } | null)?.cause;
  return typeof cause?.reason === 'string' ? cause.reason : null;
}

/**
 * "Never contact" (design outreach-v2 c1): the artist's blocks for every campaign (`outreach.blocks`).
 * A pasted link, address or website adds at once; a name lists the people Plug pitched with it.
 */
const NeverContactComponent: React.FC<{ accountId: number; readOnly: boolean }> = ({ accountId, readOnly }) => {
  const intl = useIntl();
  const { blocks, addBlock, isAdding, removeBlock } = useBlocks(accountId);
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<BlockSuggestion[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const add = useCallback(
    (input: AddBlockInput) => {
      setProblem(null);
      addBlock(input)
        .then((result) => {
          if (result.status === 'search') {
            setSuggestions(result.suggestions);
            if (result.suggestions.length === 0) {
              setProblem(intl.formatMessage(messages.neverNoMatch));
            }
            return;
          }
          setText('');
          setSuggestions(null);
        })
        .catch((error: unknown) =>
          setProblem(
            intl.formatMessage(
              reasonOf(error) === 'M24_BLOCK_UNKNOWN_PLAYLIST' ? messages.neverUnknownPlaylist : messages.neverFailed
            )
          )
        );
    },
    [addBlock, intl]
  );

  const picks = useMemo(
    () =>
      (suggestions ?? []).map((suggestion) => (
        <button
          key={suggestion.uuid}
          type="button"
          className={styles.suggestion}
          onClick={() =>
            add(
              suggestion.kind === 'contact'
                ? { kind: 'contact', workspaceContactUuid: suggestion.uuid }
                : { kind: 'playlist', targetUuid: suggestion.uuid }
            )
          }
        >
          {suggestion.label}
        </button>
      )),
    [add, suggestions]
  );
  const fixed = useMemo(
    () =>
      ALWAYS_SKIPPED.map((message) => (
        <span key={message.id} className={`${styles.ncChip} ${styles.ncFixed}`}>
          <FontAwesomeIcon icon={faLock} />
          {intl.formatMessage(message)}
        </span>
      )),
    [intl]
  );

  return (
    <QuestionPanel
      title={intl.formatMessage(messages.neverTitle)}
      hint={intl.formatMessage(messages.neverHint)}
      right={<span className={shared.small}>{intl.formatMessage(messages.neverCount, { count: blocks.length })}</span>}
      testId="never-contact"
    >
      <form
        className={styles.addInput}
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim() !== '') {
            add({ text: text.trim() });
          }
        }}
      >
        <FontAwesomeIcon icon={faBan} className={styles.addIcon} />
        <input
          value={text}
          maxLength={300}
          disabled={readOnly}
          placeholder={intl.formatMessage(messages.neverPlaceholder)}
          aria-label={intl.formatMessage(messages.neverPlaceholder)}
          onChange={(event) => setText(event.target.value)}
        />
        <SMLButton size="sm" outline omitCap submit isLoading={isAdding} disabled={readOnly || text.trim() === ''}>
          {intl.formatMessage(messages.neverAdd)}
        </SMLButton>
      </form>
      {problem !== null && <p className={shared.small}>{problem}</p>}
      {picks.length > 0 && (
        <div className={styles.chips}>
          <span className={shared.small}>{intl.formatMessage(messages.neverPick)}</span>
          {picks}
        </div>
      )}
      <NeverContactChips
        blocks={blocks}
        readOnly={readOnly}
        onRemove={(uuid) => removeBlock(uuid).catch(console.error)}
      />
      <span className={shared.eyebrow}>{intl.formatMessage(messages.alwaysSkipped)}</span>
      <div className={styles.chips}>{fixed}</div>
    </QuestionPanel>
  );
};

export const NeverContact = TypedMemo(NeverContactComponent);
